import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { StrategyEx } from "@front/m19-models";
import { SpStrategyService } from "@front/m19-services";
import { IIconComponent, IToggleComponent, ITooltipDirective, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";

import { SettingRowComponent } from "../setting-row/setting-row.component";
@Component({
  selector: "app-toggle-boost-placement-top",
  imports: [IToggleComponent, TranslocoDirective, IIconComponent, ITooltipDirective, SettingRowComponent],
  template: `@if (spStrategy(); as strategy) {
    <app-setting-row *transloco="let t">
      <ng-container label>
        {{ t("advanced-settings-modal.boost_placement_top") }}
        <IIcon
          name="infoCircle"
          class="text-muted-foreground size-4"
          [iTooltip]="t('advanced-settings-modal.boost_placement_top_tooltip')"
          iTooltipPlacement="right-center"
        />
      </ng-container>
      <IToggle
        [checked]="isBoostPlacementTopActivated()"
        [disabled]="isReadOnly()"
        (checkedChange)="activateBoostPlacementTop()"
        [loading]="isUpdating()"
      />
    </app-setting-row>
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleBoostPlacementTopComponent {
  private readonly strategyService = inject(SpStrategyService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);

  public readonly strategy = input.required<StrategyEx>();
  public readonly organizationId = input.required<number>();
  public readonly isReadOnly = input.required<boolean>();
  protected readonly isUpdating = signal(false);

  protected readonly spStrategy = computed(() => {
    const strategy = this.strategy();
    return strategy.isSpStrategy() ? strategy : undefined;
  });

  protected readonly isBoostPlacementTopActivated = linkedSignal(() => {
    return this.spStrategy()?.boostPlacementTop;
  });

  protected activateBoostPlacementTop() {
    const strategy = this.strategy();
    const newValue: boolean = !this.isBoostPlacementTopActivated();

    if (!strategy || !strategy.isSpStrategy()) return;
    this.isUpdating.set(true);
    this.strategyService
      .updateStrategyBoostPlacementTop(
        strategy.accountId,
        strategy.marketplace,
        this.organizationId()!,
        strategy.strategyId,
        newValue,
      )
      .subscribe({
        next: () => {
          if (newValue) {
            this.toasterService.success(
              this.translocoService.translate("advanced-settings-modal.boost_placement_top_activated"),
            );
          } else {
            this.toasterService.success(
              this.translocoService.translate("advanced-settings-modal.boost_placement_top_deactivated"),
            );
          }
          this.isBoostPlacementTopActivated.set(newValue);
          this.isUpdating.set(false);
        },
        error: (error) => {
          this.toasterService.error(error, this.translocoService.translate("common.error"));
          this.isUpdating.set(false);
        },
      });
  }
}
